import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { apiService } from '../services/apiService';

/**
 * Upload an older call transcript as a "historical" session. The admin pastes
 * the transcript, we parse it server-side into speaker turns, the admin maps
 * each detected speaker to salesperson / customer / ignore (multi-party: one or
 * two salespeople — two for a team-selling call — and any number of customers),
 * names the session and tags a user. We create a completed session; the
 * background analyzer grades it (as one combined score when there are two
 * salespeople, not two separate scorecards).
 */

type Role = 'salesperson' | 'customer' | 'ignore';

interface ParsedTurn {
  speaker: string;
  text: string;
  offsetSeconds: number;
}
interface ParseResult {
  speakers: string[];
  turns: ParsedTurn[];
  durationSeconds: number;
  turnCount: number;
}
interface OrgUser {
  firebaseUid: string;
  email: string;
  name?: string;
}

interface HistoricalSession {
  sessionId: string;
  sessionName: string;
  createdAt?: string;
  status?: string;
  callInsights?: { status?: string } | null;
  multipleSalespeople?: boolean;
}

const fmtDuration = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Today's date as a YYYY-MM-DD string in the user's local timezone — the
// default value for the session-date calendar picker.
const todayLocal = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const fmtDateTime = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// Derive a human "processed" status + badge style from the analysis state.
const processedStatus = (s: HistoricalSession): { label: string; cls: string } => {
  const st = s.callInsights?.status;
  if (st === 'complete') return { label: 'Analyzed', cls: 'bg-green-100 text-green-800' };
  if (st === 'pending') return { label: 'Analyzing…', cls: 'bg-blue-100 text-blue-800' };
  if (st === 'failed') return { label: 'Failed', cls: 'bg-red-100 text-red-800' };
  return { label: 'Queued', cls: 'bg-gray-100 text-gray-600' };
};

const UploadSessions: React.FC = () => {
  const navigate = useNavigate();

  const [transcriptText, setTranscriptText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const [mapping, setMapping] = useState<Record<string, Role | ''>>({});
  const [sessionName, setSessionName] = useState('');
  const [sessionDate, setSessionDate] = useState<string>(todayLocal());
  const [userId, setUserId] = useState('');

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdSessionId, setCreatedSessionId] = useState<string | null>(null);

  const [historical, setHistorical] = useState<HistoricalSession[]>([]);
  const [loadingHistorical, setLoadingHistorical] = useState(true);

  // Load org users for the tagging dropdown.
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.get('/admin/users', { limit: '200' });
        const list: any[] = res?.data?.users ?? res?.data ?? [];
        setUsers(
          list
            .filter((u) => u && (u.firebaseUid || u.uid) && u.email)
            .map((u) => ({ firebaseUid: u.firebaseUid || u.uid, email: u.email, name: u.name }))
        );
      } catch {
        /* dropdown just stays empty; surfaced on submit if needed */
      }
    })();
  }, []);

  // Load previously-uploaded historical sessions (newest first).
  const fetchHistorical = React.useCallback(async () => {
    setLoadingHistorical(true);
    try {
      const res: any = await apiService.get('/admin/sessions', {
        source: 'historical',
        limit: '50',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      const list: any[] = res?.data ?? res?.sessions ?? [];
      setHistorical(Array.isArray(list) ? list : []);
    } catch {
      setHistorical([]);
    } finally {
      setLoadingHistorical(false);
    }
  }, []);

  useEffect(() => {
    fetchHistorical();
  }, [fetchHistorical]);

  const handleParse = async () => {
    setParsing(true);
    setParseError(null);
    setParsed(null);
    setCreatedSessionId(null);
    try {
      const res: any = await apiService.post('/conversations/parse-transcript', { transcriptText });
      if (!res?.success || !res?.data) throw new Error(res?.message || 'Could not parse the transcript.');
      const data: ParseResult = res.data;
      setParsed(data);
      // Seed mapping with empty roles for each detected speaker.
      const seed: Record<string, Role | ''> = {};
      data.speakers.forEach((sp) => (seed[sp] = ''));
      setMapping(seed);
    } catch (e: any) {
      setParseError(e?.message || 'Failed to parse transcript.');
    } finally {
      setParsing(false);
    }
  };

  // Set a speaker's role. Choosing a salesperson auto-tags every unassigned
  // speaker as a customer (each keeps its transcript name) — since at most two
  // salespeople and everyone else is a customer. Any speaker can still be
  // "ignore". Up to TWO speakers may be salesperson (a team-selling call); a
  // third pick demotes whichever other salesperson appears earliest in the
  // transcript (FIFO), keeping the count capped at two.
  const handleRoleChange = (speaker: string, role: Role | '') => {
    setMapping((prev) => {
      const next = { ...prev, [speaker]: role };
      if (role === 'salesperson' && parsed) {
        const currentSalespeople = parsed.speakers.filter((sp) => next[sp] === 'salesperson');
        if (currentSalespeople.length > 2) {
          const toDemote = currentSalespeople.find((sp) => sp !== speaker);
          if (toDemote) next[toDemote] = 'customer';
        }
        // Fill any still-unassigned speakers → customer. Leave anyone already
        // explicitly set (salesperson, customer, or ignore) untouched.
        for (const other of parsed.speakers) {
          if (other === speaker) continue;
          if (!next[other]) next[other] = 'customer';
        }
      }
      return next;
    });
  };

  const allMapped = useMemo(
    () =>
      parsed != null &&
      parsed.speakers.every(
        (sp) => mapping[sp] === 'salesperson' || mapping[sp] === 'customer' || mapping[sp] === 'ignore'
      ),
    [parsed, mapping]
  );
  // One or two speakers must be the salesperson (two = a team-selling call,
  // graded as one combined composite score, not two separate scorecards).
  const salespersonNames = useMemo(
    () => (parsed ? parsed.speakers.filter((sp) => mapping[sp] === 'salesperson') : []),
    [parsed, mapping]
  );
  const salespersonCount = salespersonNames.length;
  const multipleSalespeople = salespersonCount === 2;
  const canSubmit =
    parsed != null &&
    allMapped &&
    (salespersonCount === 1 || salespersonCount === 2) &&
    sessionName.trim().length > 0 &&
    !!userId &&
    !submitting;

  const handleSubmit = async () => {
    if (!parsed || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const selected = users.find((u) => u.firebaseUid === userId);
      if (!selected) throw new Error('Please select a user to tag this session to.');
      const res: any = await apiService.post('/conversations/create-historical', {
        sessionName: sessionName.trim(),
        userId: selected.firebaseUid,
        userEmail: selected.email,
        speakerMapping: mapping,
        turns: parsed.turns,
        sessionDate,
        multipleSalespeople,
      });
      if (!res?.success) throw new Error(res?.message || 'Failed to create the session.');
      setCreatedSessionId(res.data?.sessionId || 'created');
      fetchHistorical(); // refresh the list below
    } catch (e: any) {
      setSubmitError(e?.message || 'Failed to create historical session.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setTranscriptText('');
    setParsed(null);
    setParseError(null);
    setMapping({});
    setSessionName('');
    setSessionDate(todayLocal());
    setUserId('');
    setCreatedSessionId(null);
    setSubmitError(null);
  };

  return (
    <MainLayout title="Upload Sessions">
      <div className="p-2">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Upload Sessions</h1>
          <p className="text-gray-600 mt-1">
            Upload an older call transcript as a historical session. It's graded by the
            Discovery Call Rubric automatically once created.
          </p>
        </div>

        <div className="max-w-3xl space-y-5">
        {/* Success state */}
        {createdSessionId ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
            <div className="text-2xl mb-2">📂</div>
            <h2 className="text-lg font-bold text-gray-800">Historical session created</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">
              The transcript was stored as a completed session. Post-call analysis runs
              automatically and will appear in the Call Insights tab shortly.
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <button
                onClick={() => navigate(`/sessions/${createdSessionId}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 text-sm"
              >
                View session
              </button>
              <button
                onClick={reset}
                className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50 text-sm"
              >
                Upload another
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Step 1: paste transcript */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                1 · Paste transcript
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Paste the raw transcript. Lines like <code className="bg-gray-100 px-1 rounded">0:00 - Speaker Name</code>{' '}
                followed by their text are parsed into turns; recording banners and action items are ignored.
              </p>
              <textarea
                value={transcriptText}
                onChange={(e) => setTranscriptText(e.target.value)}
                rows={12}
                placeholder={'0:00 - Brian Kavicky (Sandler)\n  Good morning. How are you?\n\n0:02 - Michael Warady, CFP\n  Excellent.'}
                className="w-full font-mono text-xs border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={handleParse}
                  disabled={parsing || !transcriptText.trim()}
                  className="bg-gray-800 text-white px-4 py-2 rounded font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {parsing ? 'Parsing…' : 'Parse transcript'}
                </button>
                {parseError && <span className="text-sm text-red-600">{parseError}</span>}
              </div>
            </div>

            {/* Step 2: map speakers + details */}
            {parsed && (
              <>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                    2 · Who is who?
                  </div>
                  <p className="text-sm text-gray-500 mb-3">
                    Parsed <b>{parsed.turnCount}</b> turns ({fmtDuration(parsed.durationSeconds)}).
                    Pick the <b>salesperson</b> — or <b>two</b>, for a team-selling call — everyone
                    else is auto-tagged as a customer, keeping their name from the transcript (set a
                    speaker to <b>Ignore</b> to drop it).
                  </p>
                  <div className="space-y-2">
                    {parsed.speakers.map((sp) => (
                      <div key={sp} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-800 truncate">{sp}</span>
                        <select
                          value={mapping[sp] || ''}
                          onChange={(e) => handleRoleChange(sp, e.target.value as Role | '')}
                          className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-400 outline-none"
                        >
                          <option value="">— select role —</option>
                          <option value="salesperson">Salesperson</option>
                          <option value="customer">Customer</option>
                          <option value="ignore">Ignore</option>
                        </select>
                      </div>
                    ))}
                  </div>
                  {parsed && allMapped && salespersonCount !== 1 && salespersonCount !== 2 && (
                    <p className="text-xs text-red-600 mt-2">
                      {salespersonCount === 0
                        ? 'Assign at least one speaker as the salesperson.'
                        : 'At most two speakers can be the salesperson.'}
                    </p>
                  )}
                  {multipleSalespeople && (
                    <p className="text-xs text-blue-700 mt-2">
                      Team-selling call — {salespersonNames.join(' and ')} will be graded as one
                      combined score, not two separate scorecards.
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    3 · Session details
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Session name</label>
                    <input
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="e.g. Measurable Pain Step — Michael (Tri-Merit)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Session date</label>
                    <input
                      type="date"
                      value={sessionDate}
                      max={todayLocal()}
                      onChange={(e) => setSessionDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The date this call happened. Used as the session's created date. Defaults to today.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {multipleSalespeople ? 'Tag to user (primary salesperson / session owner)' : 'Tag to user (salesperson)'}
                    </label>
                    <select
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none"
                    >
                      <option value="">— select user —</option>
                      {users.map((u) => (
                        <option key={u.firebaseUid} value={u.firebaseUid}>
                          {u.name ? `${u.name} (${u.email})` : u.email}
                        </option>
                      ))}
                    </select>
                    {users.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No users loaded — check that you have admin access.</p>
                    )}
                    {multipleSalespeople && (
                      <p className="text-xs text-gray-500 mt-1">
                        This dropdown governs ownership and reporting rollups only. Both salespeople
                        ({salespersonNames.join(' and ')}) are identified by their transcript name in
                        the call itself — the one not selected above is not tied to a platform account.
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="bg-blue-600 text-white px-5 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {submitting ? 'Creating…' : 'Create historical session'}
                    </button>
                    {submitError && <span className="text-sm text-red-600">{submitError}</span>}
                  </div>
                </div>
              </>
            )}
          </>
        )}
        </div>

        {/* Previously-uploaded historical sessions */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">
              Historical uploads
            </h2>
            <button onClick={fetchHistorical} className="text-xs text-blue-600 hover:underline">
              Refresh
            </button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loadingHistorical ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
            ) : historical.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No historical sessions uploaded yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-semibold">Session</th>
                    <th className="px-4 py-2 font-semibold w-48">Uploaded</th>
                    <th className="px-4 py-2 font-semibold w-32">Analysis</th>
                  </tr>
                </thead>
                <tbody>
                  {historical.map((s) => {
                    const ps = processedStatus(s);
                    return (
                      <tr
                        key={s.sessionId}
                        onClick={() => navigate(`/sessions/${s.sessionId}`)}
                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-2.5 text-gray-800 font-medium">
                          {s.sessionName}
                          {s.multipleSalespeople && (
                            <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 text-purple-700 align-middle">
                              2 SP
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(s.createdAt)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${ps.cls}`}>
                            {ps.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default UploadSessions;
